import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useATS } from "../context/ATSContext";
import {
  departmentService,
  isEndpointMissing,
  readApiError,
} from "../services/departmentService";
import OrgCanvas from "../components/departments/OrgCanvas";
import NodeDetailPanel from "../components/departments/NodeDetailPanel";
import NodeFormDialog from "../components/departments/NodeFormDialog";
import MoveNodeDialog from "../components/departments/MoveNodeDialog";
import DepartmentFormDrawer from "../components/departments/DepartmentFormDrawer";
import JobCreationForm from "../components/JobCreationModal";
import { buildTree, findNode } from "../components/departments/treeUtils";
import { useDeptRoutes } from "../components/departments/deptRoutes";
import {
  DeptTheme,
  EmptyState,
  Hint,
  PrimaryButton,
  QuietButton,
  StatPill,
  T,
} from "../components/departments/ui";

// The org chart, serving two routes off one surface:
//
//   /ats/org                    every department as its own tree
//   /ats/departments/:deptId    one department, same canvas
//
// and the same two again under /create-department/org, where the Daily Tick
// shell mounts this screen so its sidebar's Departments entry reaches the org
// chart without leaving that shell. Every link below therefore goes through
// `routes` rather than a literal path — see deptRoutes.js.
//
// It replaces the two-pane tree-plus-tabs builder. The detail panel is unchanged
// — it moved from a fixed column into a side sheet, which is what freed the
// whole width for the diagram.

const OrgChart = () => {
  const { deptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useDeptRoutes();

  // Where the back arrow goes.
  //
  // A department's chart is reached from two different screens — the card list
  // and the all-departments chart — so a fixed target is wrong from one of them.
  // It used to always go to the all-departments chart, which meant drilling in
  // from the list and pressing back left you on a third screen you had never
  // asked for, with the list nowhere in reach.
  //
  // Whoever navigates here puts the URL it came from in history state, so back
  // returns there. `routes.back` covers a refresh or a pasted link, where there
  // is no state to read.
  const backTo = location.state?.from || routes.back;
  const { store_id, user_id } = useATS();

  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiMissing, setApiMissing] = useState(false);
  const [search, setSearch] = useState("");

  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const [nodeDialog, setNodeDialog] = useState({
    open: false,
    mode: "create",
    parentNode: null,
    node: null,
  });
  const [moveTarget, setMoveTarget] = useState(null);
  const [deptDrawer, setDeptDrawer] = useState(null);
  const [jobFromTemplate, setJobFromTemplate] = useState(null);

  const load = useCallback(async () => {
    if (store_id === undefined) return;
    setLoading(true);
    try {
      // One read covers both routes: the single-department view is a filter, not
      // a different request, so navigating between them costs nothing.
      const res = await departmentService.getOrgStructure({
        includeEmpty: !!deptId,
      });
      setStructure(res?.data?.data || []);
      setError("");
      setApiMissing(false);
    } catch (err) {
      if (isEndpointMissing(err)) {
        setApiMissing(true);
        setStructure([]);
        setError("");
      } else {
        setError(readApiError(err, "Could not load the org chart."));
      }
    } finally {
      setLoading(false);
    }
  }, [store_id, deptId]);

  useEffect(() => {
    load();
  }, [load]);

  const scoped = useMemo(
    () =>
      deptId
        ? structure.filter(
            (entry) => Number(entry.department?.id) === Number(deptId),
          )
        : structure,
    [structure, deptId],
  );

  // Each department becomes the root of its own tree, with its positions
  // beneath. buildTree treats a node whose parent is absent from the list as a
  // root, which is exactly the first tier of positions.
  const forest = useMemo(
    () =>
      scoped.map((entry) => ({
        ...entry.department,
        node_type: "dept",
        members: entry.department_members || [],
        // The count on a DEPARTMENT is everyone in it — the people in its
        // positions plus those attached to the department directly, counted
        // once each. department_members alone is only the second group, which
        // is usually a handful and would badly understate the department.
        // The server already de-duplicates this by user_id.
        member_count: entry.totals?.people ?? (entry.department_members || []).length,
        job_count: entry.totals?.open_jobs || 0,
        template_count: 1, // a department is never "needs a template"
        children: buildTree(entry.nodes || []),
        __entry: entry,
      })),
    [scoped],
  );

  const flatNodes = useMemo(
    () => scoped.flatMap((entry) => entry.nodes || []),
    [scoped],
  );

  // Search does not filter the diagram — hiding branches would break the shape
  // people are reading. It selects and reveals the match instead.
  const searchHits = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return flatNodes
      .filter(
        (node) =>
          node.name?.toLowerCase().includes(term) ||
          (node.members || []).some((m) => m.name?.toLowerCase().includes(term)),
      )
      .slice(0, 6);
  }, [flatNodes, search]);

  const selectedNode = useMemo(() => {
    if (!selected) return null;
    if (selected.__isDept) return selected;
    return findNode(forest, selected.id) || selected;
  }, [forest, selected]);

  const isDeptSelected = !!selectedNode && !selectedNode.parent_id;

  // The chain from the selection up to its department, for lighting the links.
  const ancestorIds = useMemo(() => {
    if (!selectedNode) return new Set();
    const ids = String(selectedNode.path || "")
      .split("/")
      .filter(Boolean)
      .map(Number);
    return new Set(ids);
  }, [selectedNode]);

  // The department the selection belongs to, which every write needs.
  const owningEntry = useMemo(() => {
    if (!selectedNode) return null;
    const rootId = Number(
      String(selectedNode.path || selectedNode.id).split("/")[0],
    );
    return scoped.find((entry) => Number(entry.department?.id) === rootId) || null;
  }, [scoped, selectedNode]);

  const breadcrumb = useMemo(() => {
    if (!selectedNode || !owningEntry) return [];
    const byId = new Map(
      (owningEntry.nodes || []).map((node) => [Number(node.id), node]),
    );
    const ids = String(selectedNode.path || "")
      .split("/")
      .filter(Boolean)
      .map(Number);
    return ids.map((id, idx) =>
      idx === 0
        ? { id, name: owningEntry.department?.name || "Department" }
        : byId.get(id) || { id, name: "…" },
    );
  }, [selectedNode, owningEntry]);

  const toggleCollapse = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const collapseAll = () => {
    const ids = new Set();
    const walk = (list) =>
      list.forEach((node) => {
        if ((node.children || []).length) {
          ids.add(node.id);
          walk(node.children);
        }
      });
    walk(forest);
    setCollapsed(ids);
  };

  const handleSelect = (node) => {
    setSelected(
      node.parent_id ? node : { ...node, __isDept: true },
    );
    // Revealing a collapsed selection would be surprising the other way round:
    // clicking a collapsed node should show its details, not silently expand it.
  };

  const openCreate = (parentNode) =>
    setNodeDialog({ open: true, mode: "create", parentNode, node: null });
  const openEdit = (node) =>
    setNodeDialog({ open: true, mode: "edit", parentNode: null, node });

  const handleDelete = async (node) => {
    const below = (node.children || []).length;
    if (
      !window.confirm(
        below
          ? `Delete "${node.name}" and everything under it?\n\nJob templates go back to the library. People keep every other place they belong to.`
          : `Delete "${node.name}"?\n\nJob templates go back to the library. People keep every other place they belong to.`,
      )
    )
      return;
    try {
      await departmentService.deleteNode(node.id, { cascade: below > 0 });
      if (selectedNode?.id === node.id) setSelected(null);
      await load();
    } catch (err) {
      setError(
        readApiError(
          err,
          "Could not delete this. Close or move its open jobs first.",
        ),
      );
    }
  };

  if (store_id === undefined)
    return <Hint>Sign in to view the org chart.</Hint>;

  const singleEntry = deptId ? scoped[0] : null;

  if (jobFromTemplate) {
    return (
      <DeptTheme>
        <Box sx={{ fontFamily: T.font }}>
          <QuietButton
            onClick={() => setJobFromTemplate(null)}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{ mb: 1.5 }}
          >
            Back to the chart
          </QuietButton>
          <JobCreationForm
            initialTemplateId={jobFromTemplate.id}
            deptContext={{
              department_id: Number(owningEntry?.department?.id),
              dept_node_id: selectedNode?.id || null,
              department_name: owningEntry?.department?.name,
              breadcrumb: breadcrumb.map((item) => item.name).join(" › "),
            }}
            onCreated={() => {
              setJobFromTemplate(null);
              load();
            }}
            onUpdated={() => {
              setJobFromTemplate(null);
              load();
            }}
            onCancel={() => setJobFromTemplate(null)}
          />
        </Box>
      </DeptTheme>
    );
  }

  return (
    <DeptTheme>
      <Box sx={{ fontFamily: T.font }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          {deptId && (
            <Tooltip title="Back">
              <IconButton
                onClick={() => navigate(backTo)}
                size="small"
                aria-label="Back"
                sx={{ color: T.muted, mt: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: 23, sm: 27 },
                letterSpacing: "-0.02em",
                color: T.ink,
                lineHeight: 1.15,
              }}
            >
              {singleEntry?.department?.name || "Org chart"}
            </Typography>
            <Hint sx={{ mt: 0.25 }}>
              {deptId
                ? "Click any box to edit it, add below it, or open a job."
                : "Every department, its positions, and who sits on each."}
            </Hint>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", position: "relative" }}>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find a position or person"
              size="small"
              sx={{ minWidth: { xs: 190, sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: T.faint }} />
                  </InputAdornment>
                ),
                sx: {
                  fontFamily: T.font,
                  bgcolor: T.surface,
                  borderRadius: T.radius.md,
                },
              }}
            />
            <PrimaryButton
              startIcon={<AddIcon />}
              onClick={() =>
                singleEntry
                  ? openCreate(null)
                  : navigate(routes.list)
              }
            >
              {singleEntry ? "Add position" : "Departments"}
            </PrimaryButton>

            {/* Results float over the canvas: filtering the diagram would break
                the shape people are reading, so a hit is selected instead. */}
            {searchHits.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  zIndex: 5,
                  minWidth: 280,
                  bgcolor: T.surface,
                  borderRadius: T.radius.md,
                  boxShadow: "0 10px 30px rgba(15,23,42,.16)",
                  overflow: "hidden",
                }}
              >
                {searchHits.map((hit) => (
                  <Box
                    key={hit.id}
                    onClick={() => {
                      handleSelect(hit);
                      setSearch("");
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSelect(hit);
                        setSearch("");
                      }
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: T.accentWash },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: T.font,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: T.ink,
                      }}
                    >
                      {hit.name}
                    </Typography>
                    <Hint sx={{ fontSize: 11.5 }}>
                      {(hit.members || []).map((m) => m.name).join(", ") ||
                        "Nobody assigned"}
                    </Hint>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {apiMissing && (
          <Alert
            severity="info"
            sx={{ fontFamily: T.font, mb: 2, borderRadius: T.radius.md }}
          >
            This API does not have the org structure endpoint yet.
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ fontFamily: T.font, mb: 2, borderRadius: T.radius.md }}
            onClose={() => setError("")}
            action={
              <QuietButton size="small" onClick={load}>
                Try again
              </QuietButton>
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 12 }}>
            <CircularProgress size={26} sx={{ color: T.accent }} />
          </Box>
        ) : forest.length === 0 ? (
          <Box sx={{ bgcolor: T.surface, borderRadius: T.radius.lg }}>
            <EmptyState
              icon={<AccountTreeOutlinedIcon />}
              title="Nothing to chart yet"
              body="Once a department has positions under it, the chart draws itself."
              action={
                <PrimaryButton onClick={() => navigate(routes.list)}>
                  Go to departments
                </PrimaryButton>
              }
            />
          </Box>
        ) : (
          <>
            {singleEntry && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  flexWrap: "wrap",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <StatPill
                  label="positions"
                  value={singleEntry.totals?.positions ?? 0}
                />
                <StatPill label="people" value={singleEntry.totals?.people ?? 0} />
                <StatPill
                  label="job templates"
                  value={singleEntry.totals?.templates ?? 0}
                />
                {(singleEntry.totals?.open_jobs ?? 0) > 0 && (
                  <StatPill
                    label="open jobs"
                    value={singleEntry.totals.open_jobs}
                    tone="ok"
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <QuietButton
                  size="small"
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setDeptDrawer(singleEntry.department)}
                >
                  Edit department
                </QuietButton>
              </Box>
            )}

            <OrgCanvas
              roots={forest}
              selectedId={selectedNode?.id}
              onSelect={handleSelect}
              collapsed={collapsed}
              onToggleCollapse={toggleCollapse}
              onCollapseAll={collapseAll}
              onExpandAll={() => setCollapsed(new Set())}
              ancestorIds={ancestorIds}
              height={deptId ? 620 : 700}
            />
          </>
        )}

        {/* Detail as a sheet rather than a permanent column: the diagram is the
            screen, and a fixed panel took a third of the width whether or not
            anything was selected. */}
        <Drawer
          anchor="right"
          open={!!selectedNode}
          onClose={() => setSelected(null)}
          PaperProps={{ sx: { width: "100%", maxWidth: 540 } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              pt: 1.5,
              pb: 0.5,
            }}
          >
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {selectedNode && isDeptSelected ? (
            <Box sx={{ px: 3, pb: 3 }}>
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontWeight: 800,
                  fontSize: 20,
                  color: T.ink,
                }}
              >
                {selectedNode.name}
              </Typography>
              <Hint sx={{ mb: 2 }}>Department</Hint>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2.5 }}>
                <StatPill
                  label="positions"
                  value={selectedNode.__entry?.totals?.positions ?? 0}
                />
                <StatPill
                  label="people"
                  value={selectedNode.__entry?.totals?.people ?? 0}
                />
                <StatPill
                  label="open jobs"
                  value={selectedNode.__entry?.totals?.open_jobs ?? 0}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => {
                    openCreate(null);
                  }}
                >
                  Add a position
                </PrimaryButton>
                <QuietButton
                  onClick={() => setDeptDrawer(selectedNode)}
                >
                  Edit department
                </QuietButton>
                {!deptId && (
                  <QuietButton
                    onClick={() =>
                      navigate(routes.detail(selectedNode.id), {
                        state: { from: routes.chart },
                      })
                    }
                  >
                    Focus this one
                  </QuietButton>
                )}
              </Box>
            </Box>
          ) : selectedNode ? (
            <NodeDetailPanel
              node={selectedNode}
              departmentId={owningEntry?.department?.id}
              breadcrumb={breadcrumb}
              store_id={store_id}
              user_id={user_id}
              onEditNode={openEdit}
              onAddChild={openCreate}
              onTreeChanged={load}
              onCreateJob={(template) => setJobFromTemplate(template)}
            />
          ) : null}

          {selectedNode && !isDeptSelected && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 3,
                py: 2,
                flexWrap: "wrap",
              }}
            >
              <QuietButton size="small" onClick={() => setMoveTarget(selectedNode)}>
                Move
              </QuietButton>
              <QuietButton
                size="small"
                onClick={() => handleDelete(selectedNode)}
                sx={{ color: T.danger }}
              >
                Delete
              </QuietButton>
            </Box>
          )}
        </Drawer>

        <NodeFormDialog
          open={nodeDialog.open}
          onClose={() => setNodeDialog((prev) => ({ ...prev, open: false }))}
          mode={nodeDialog.mode}
          departmentId={
            owningEntry?.department?.id || singleEntry?.department?.id || deptId
          }
          parentNode={nodeDialog.parentNode}
          node={nodeDialog.node}
          breadcrumb={breadcrumb}
          store_id={store_id}
          user_id={user_id}
          onSaved={load}
        />

        <MoveNodeDialog
          open={!!moveTarget}
          onClose={() => setMoveTarget(null)}
          node={moveTarget}
          roots={
            (owningEntry ? buildTree(owningEntry.nodes || []) : []) || []
          }
          departmentName={owningEntry?.department?.name}
          onMoved={load}
        />

        <DepartmentFormDrawer
          open={!!deptDrawer}
          onClose={() => setDeptDrawer(null)}
          department={deptDrawer}
          store_id={store_id}
          user_id={user_id}
          onSaved={() => {
            setDeptDrawer(null);
            load();
          }}
        />

        {!loading && forest.length > 0 && !deptId && (
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {forest.map((root) => (
              <Chip
                key={root.id}
                label={root.name}
                onClick={() =>
                  navigate(routes.detail(root.id), {
                    state: { from: routes.chart },
                  })
                }
                sx={{
                  fontFamily: T.font,
                  fontWeight: 600,
                  fontSize: 12.5,
                  cursor: "pointer",
                  bgcolor: T.surface,
                  color: T.body,
                  "&:hover": { bgcolor: T.accentWash, color: T.accentDeep },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </DeptTheme>
  );
};

export default OrgChart;
